import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  Api,
  canDeleteContent,
  canManageContentActivation,
  CHAT_LESSON_TOPIC_OPTIONS,
  hasPermission,
  IChatLesson,
  PERMISSIONS,
} from "@model";
import { SearchBar, ISearchQuery } from "@components";
import { useIsCompact } from "../../hooks/useIsCompact";
import { Button, Checkbox, Empty, Pagination, Spin, Table, Tag, message, Modal } from "antd";
import { getLessonListTitle } from "./chatLessonLanguages";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { ColumnsType } from "antd/lib/table";
import { useSearchParams } from "react-router-dom";

const ChatLessonListPage = () => {
  const isCompact = useIsCompact();
  const [searchParams, setSearchParams] = useSearchParams();
  const [lessons, setLessons] = useState<IChatLesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [sortField, setSortField] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [searchQuery, setSearchQuery] = useState<ISearchQuery>({
    q: searchParams.get("q") ?? "",
  });

  const syncStateFromUrl = useCallback(() => {
    const q = searchParams.get("q") ?? "";
    setSearchQuery((prev) => (prev.q !== q ? { q } : prev));
    setCurrentPage(Math.max(1, Number(searchParams.get("page")) || 1));
    const ps = Number(searchParams.get("pageSize"));
    if (ps) setPageSize(Math.min(100, Math.max(10, ps)));
    const sf = searchParams.get("sortField");
    setSortField(sf || undefined);
    const so = searchParams.get("sortOrder");
    setSortOrder(so || undefined);
  }, [searchParams]);

  useEffect(() => {
    syncStateFromUrl();
  }, [syncStateFromUrl]);

  const fetchLessons = useCallback(() => {
    setLoading(true);
    Api.get(`/api/admin/panel/chat-lessons`, {
      params: {
        title: searchQuery.q,
        sortField,
        sortOrder,
        page: currentPage,
        limit: pageSize,
      },
    })
      .then((response) => {
        const data = response.data.data;
        setLessons(data);
        setLoading(false);
        const currentDataCount = data.length;
        if (currentDataCount < pageSize) {
          setTotalPages(currentPage);
        } else {
          setTotalPages(currentPage + 1);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        message.error("Failed to fetch chat lessons");
        setLoading(false);
      });
  }, [searchQuery.q, sortField, sortOrder, currentPage, pageSize]);

  useEffect(() => {
    fetchLessons();
  }, [fetchLessons]);

  const updateUrl = (updates: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        next.set(key, value);
      } else {
        next.delete(key);
      }
    });
    setSearchParams(next, { replace: true });
  };

  const handlePaginationChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize) setPageSize(newPageSize);
    updateUrl({
      page: String(page),
      ...(newPageSize ? { pageSize: String(newPageSize) } : {}),
    });
  };

  const navigate = useNavigate();
  const canEdit = hasPermission(PERMISSIONS.chatLessonsEdit);
  const canCreate = hasPermission(PERMISSIONS.chatLessonsCreate);
  const canActivate = canManageContentActivation();

  const updateActivation = useCallback(async (record: IChatLesson, isActive: boolean) => {
    await Api.patch(`/api/admin/panel/chat-lessons/${record._id}/activation`, {
      isActive,
    });
    setLessons((current) =>
      current.map((item) =>
        item._id === record._id ? { ...item, isActive } : item,
      ),
    );
  }, []);

  const handleDelete = useCallback(
    (record: IChatLesson) => {
      Modal.confirm({
        title: "Delete Chat Lesson",
        content: `Are you sure you want to delete "${getLessonListTitle(record)}"?`,
        okText: "Delete",
        okType: "danger",
        cancelText: "Cancel",
        onOk: async () => {
          try {
            await Api.delete(`/api/admin/panel/chat-lessons/${record._id}`);
            message.success("Lesson deleted successfully");
            fetchLessons();
          } catch (error) {
            message.error("Failed to delete lesson");
          }
        },
      });
    },
    [fetchLessons],
  );

  const columns = useMemo(
    (): ColumnsType<any> => [
      {
        title: "Active",
        width: 50,
        dataIndex: "isActive",
        key: "isActive",
        render: (isActive: boolean, record: IChatLesson) => (
          <Checkbox
            checked={isActive === true}
            disabled={!canActivate}
            onChange={(event) =>
              void updateActivation(record, event.target.checked)
            }
          />
        ),
      },
      {
        title: "Premium",
        width: 50,
        dataIndex: "isPremium",
        key: "isPremium",
        render: (w: boolean) => <Checkbox checked={w === true} disabled />,
      },
      {
        title: "Journey",
        width: 60,
        dataIndex: "isForJourney",
        key: "isForJourney",
        render: (w: boolean) => <Checkbox checked={w === true} disabled />,
      },
      {
        title: "Title",
        width: 250,
        key: "title",
        render: (_: unknown, record: IChatLesson) =>
          canEdit ? (
          <a
            href={`/panel/chat-lessons/create/${record._id ?? ""}`}
            onClick={(e) => {
              e.preventDefault();
              const returnTo = searchParams.toString()
                ? `/panel/chat-lessons?${searchParams.toString()}`
                : "/panel/chat-lessons";
              navigate(`/panel/chat-lessons/create/${record._id ?? ""}`, {
                state: { ...record, returnTo },
              });
            }}
            style={{
              fontWeight: 600,
              color: "#1890ff",
              textDecoration: "underline",
            }}
          >
            {getLessonListTitle(record)}
          </a>
          ) : (
            getLessonListTitle(record)
          ),
      },
      {
        title: "Topic",
        width: 150,
        dataIndex: "topic",
        key: "topic",
      },
      {
        title: "Steps",
        width: 80,
        dataIndex: "steps",
        key: "steps",
        render: (steps: any[]) => steps?.length || 0,
      },
      {
        title: "Creator",
        width: 140,
        key: "creator",
        render: (_: unknown, record: IChatLesson) => record.creator?.name ?? "-",
      },
      {
        title: "Actions",
        width: 150,
        key: "actions",
        render: (_: any, record: IChatLesson) => (
          <div style={{ display: "flex", gap: 8 }}>
            {canEdit && <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => {
                const returnTo = searchParams.toString()
                  ? `/panel/chat-lessons?${searchParams.toString()}`
                  : "/panel/chat-lessons";
                navigate(`/panel/chat-lessons/create/${record._id}`, {
                  state: { ...record, returnTo },
                });
              }}
            >
              Edit
            </Button>}
            {canDeleteContent(
              record.creator,
              PERMISSIONS.chatLessonsDelete,
              PERMISSIONS.chatLessonsDeleteOwn,
            ) && <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            >
              Delete
            </Button>}
          </div>
        ),
      },
    ],
    [
      searchParams,
      navigate,
      handleDelete,
      canActivate,
      canEdit,
      updateActivation,
    ],
  );

  return (
    <>
      <div>
        <div
          style={{
            display: "flex",
            flexDirection: isCompact ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isCompact ? "stretch" : "center",
            gap: 8,
            marginBottom: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <SearchBar
              style={{ width: "100%", margin: 0 }}
              value={searchQuery.q}
              onChange={(e) => {
                setSearchQuery(e);
                setCurrentPage(1);
                setLoading(true);
                updateUrl({ q: e.q || undefined, page: "1" });
              }}
              searchType="Enter"
            />
          </div>
          {canCreate && <Button
            type="primary"
            icon={<PlusOutlined />}
            style={isCompact ? { width: "100%" } : undefined}
            onClick={() => {
              const returnTo = searchParams.toString()
                ? `/panel/chat-lessons?${searchParams.toString()}`
                : "/panel/chat-lessons";
              navigate(`/panel/chat-lessons/create`, { state: { returnTo } });
            }}
          >
            New
          </Button>}
        </div>
        {isCompact ? (
          <>
            <Spin spinning={loading}>
              {lessons.length === 0 && !loading ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No chat lessons" />
              ) : (
                lessons.map((record) => {
                  const title = getLessonListTitle(record);
                  const topicLabel =
                    CHAT_LESSON_TOPIC_OPTIONS.find((option) => option.value === record.topic)
                      ?.label ?? record.topic;
                  const returnTo = searchParams.toString()
                    ? `/panel/chat-lessons?${searchParams.toString()}`
                    : "/panel/chat-lessons";
                  const canDelete = canDeleteContent(
                    record.creator,
                    PERMISSIONS.chatLessonsDelete,
                    PERMISSIONS.chatLessonsDeleteOwn,
                  );
                  return (
                    <div
                      key={record._id}
                      style={{
                        background: "#fff",
                        border: "1px solid #f0f0f0",
                        borderRadius: 8,
                        padding: 12,
                        marginBottom: 8,
                        color: record.isDeleted ? "#de0000" : undefined,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 8,
                          alignItems: "flex-start",
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          {canEdit ? (
                            <a
                              href={`/panel/chat-lessons/create/${record._id ?? ""}`}
                              onClick={(e) => {
                                e.preventDefault();
                                navigate(`/panel/chat-lessons/create/${record._id ?? ""}`, {
                                  state: { ...record, returnTo },
                                });
                              }}
                              style={{
                                fontWeight: 600,
                                color: record.isDeleted ? "#de0000" : "#1890ff",
                                wordBreak: "break-word",
                              }}
                            >
                              {title}
                            </a>
                          ) : (
                            <span style={{ fontWeight: 600, wordBreak: "break-word" }}>
                              {title}
                            </span>
                          )}
                          <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                            {record.creator?.name ?? "No creator"}
                          </div>
                        </div>
                        <div style={{ display: "flex" }}>
                          {canEdit && (
                            <Button
                              type="link"
                              icon={<EditOutlined />}
                              onClick={() =>
                                navigate(`/panel/chat-lessons/create/${record._id}`, {
                                  state: { ...record, returnTo },
                                })
                              }
                            />
                          )}
                          {canDelete && (
                            <Button
                              type="link"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => handleDelete(record)}
                            />
                          )}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "4px 14px",
                          marginTop: 8,
                          alignItems: "center",
                        }}
                      >
                        <label style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <Checkbox
                            checked={record.isActive === true}
                            disabled={!canActivate}
                            onChange={(event) =>
                              void updateActivation(record, event.target.checked)
                            }
                          />
                          Active
                        </label>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <Checkbox checked={record.isPremium === true} disabled />
                          Premium
                        </span>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <Checkbox checked={record.isForJourney === true} disabled />
                          Journey
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 6,
                          marginTop: 8,
                          alignItems: "center",
                        }}
                      >
                        {topicLabel && <Tag style={{ margin: 0 }}>{topicLabel}</Tag>}
                        <span style={{ fontSize: 12, color: "#666" }}>
                          {record.steps?.length || 0} steps
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </Spin>
            {(totalPages > 1 || currentPage > 1) && (
              <Pagination
                style={{ marginTop: 8, textAlign: "center" }}
                size="small"
                simple
                current={currentPage}
                pageSize={pageSize}
                total={totalPages * pageSize}
                onChange={handlePaginationChange}
              />
            )}
          </>
        ) : (
        <Table
          loading={loading}
          size="small"
          pagination={{
            current: currentPage,
            pageSize,
            total: totalPages * pageSize,
            onChange: handlePaginationChange,
          }}
          rowKey={(record) => record._id ?? ""}
          rowClassName={(us) => (us.isDeleted === true ? "deleted-record" : "")}
          columns={columns}
          dataSource={lessons}
          onChange={(pagination, filters, sorter) => {
            const urlUpdates: Record<string, string | undefined> = {};
            if (!Array.isArray(sorter) && sorter?.field != null) {
              const sf = String(sorter.field);
              const so = sorter.order === "ascend" ? "asc" : "desc";
              setSortField(sf);
              setSortOrder(so);
              urlUpdates.sortField = sf;
              urlUpdates.sortOrder = so;
            }
            updateUrl(urlUpdates);
          }}
        />
        )}
      </div>
    </>
  );
};

export default ChatLessonListPage;
